import React, { useState, useEffect, useRef } from 'react';
import {
    View,
    Text,
    StyleSheet,
    ScrollView,
    TouchableOpacity,
    TextInput,
    KeyboardAvoidingView,
    Platform,
    Linking,
    Alert,
    ActivityIndicator,
    StatusBar,
} from 'react-native';
import axios from 'axios';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { Feather } from '@expo/vector-icons';
import { COLORS, API } from '../config';
import { useAuth } from '../context/AuthContext';

const ADMIN_WA = '6281234567890'; // Ganti dengan nomor WA admin

export default function BantuanScreen({ navigation }) {
    const { user } = useAuth();
    const insets = useSafeAreaInsets();
    const [messages, setMessages] = useState([]);
    const [inputText, setInputText] = useState('');
    const [loading, setLoading] = useState(true);
    const [sending, setSending] = useState(false);
    const scrollViewRef = useRef();

    useEffect(() => {
        fetchMessages();
        // Auto refresh every 5 seconds
        const interval = setInterval(fetchMessages, 5000);
        return () => clearInterval(interval);
    }, []);

    const fetchMessages = async () => {
        try {
            const response = await axios.get(API.CHAT.GET_MESSAGES(user.id));
            if (response.data.success) {
                setMessages(response.data.data || []);
            }
        } catch (error) {
            console.error('Fetch messages error:', error);
        } finally {
            setLoading(false);
        }
    };

    const sendMessage = async () => {
        if (!inputText.trim()) return;

        setSending(true);
        try {
            const response = await axios.post(
                API.CHAT.SEND_MESSAGE(user.id),
                { message: inputText.trim() },
                { headers: { 'Content-Type': 'application/json' } }
            );

            if (response.data.success) {
                setInputText('');
                fetchMessages();
            } else {
                Alert.alert('Error', response.data.message || 'Gagal mengirim pesan');
            }
        } catch (error) {
            console.error('Send message error:', error);
            Alert.alert('Error', 'Gagal mengirim pesan');
        } finally {
            setSending(false);
        }
    };

    const openWhatsApp = () => {
        const message = encodeURIComponent(`Halo Admin Rental PS Ming!\nSaya ${user.nama} (${user.kode_member})`);
        const url = `whatsapp://send?phone=${ADMIN_WA}&text=${message}`;

        Linking.canOpenURL(url)
            .then((supported) => {
                if (supported) {
                    Linking.openURL(url);
                } else {
                    Alert.alert('Error', 'WhatsApp tidak terinstall');
                }
            });
    };

    const formatTime = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit' });
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        const today = new Date();
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);

        if (date.toDateString() === today.toDateString()) return 'Hari ini';
        if (date.toDateString() === yesterday.toDateString()) return 'Kemarin';
        return date.toLocaleDateString('id-ID', { day: 'numeric', month: 'short' });
    };

    return (
        <View style={styles.container}>
            <StatusBar barStyle="dark-content" backgroundColor={COLORS.bgDarker} />

            {/* Enhanced Header */}
            <View style={[styles.header, { paddingTop: insets.top + 12 }]}>
                <TouchableOpacity
                    style={styles.backButton}
                    onPress={() => navigation.goBack()}
                >
                    <Feather name="arrow-left" size={24} color={COLORS.textPrimary} />
                </TouchableOpacity>
                <View style={styles.headerContent}>
                    <Text style={styles.headerTitle}>Bantuan & Support</Text>
                    <Text style={styles.headerSubtitle}>Kami siap membantu Anda</Text>
                </View>
            </View>

            <ScrollView
                showsVerticalScrollIndicator={false}
                style={styles.scrollView}
                contentContainerStyle={styles.scrollViewContent}
            >
                {/* Enhanced WhatsApp Card */}
                <TouchableOpacity
                    style={styles.waCard}
                    activeOpacity={0.8}
                    onPress={openWhatsApp}
                >
                    <View style={styles.waCardGradient}>
                        <View style={styles.waIconContainer}>
                            <View style={styles.waIcon}>
                                <Feather name="message-circle" size={28} color="#fff" />
                            </View>
                        </View>
                        <View style={styles.waContent}>
                            <Text style={styles.waTitle}>Chat via WhatsApp</Text>
                            <Text style={styles.waNumber}>+62 812-3456-7890</Text>
                            <Text style={styles.waDesc}>Respon cepat dari admin</Text>
                        </View>
                        <View style={styles.waArrowContainer}>
                            <Feather name="chevron-right" size={24} color="rgba(255,255,255,0.8)" />
                        </View>
                    </View>
                </TouchableOpacity>

                {/* Enhanced Live Chat Section */}
                <View style={styles.chatSection}>
                    <View style={styles.sectionHeader}>
                        <View style={styles.sectionIconContainer}>
                            <Feather name="message-square" size={20} color={COLORS.primary} />
                        </View>
                        <View>
                            <Text style={styles.sectionTitle}>Live Chat dengan Admin</Text>
                            <Text style={styles.sectionSubtitle}>Tanyakan apapun yang ingin Anda ketahui</Text>
                        </View>
                    </View>

                    {loading ? (
                        <View style={styles.loadingContainer}>
                            <ActivityIndicator size="large" color={COLORS.primary} />
                            <Text style={styles.loadingText}>Memuat pesan...</Text>
                        </View>
                    ) : (
                        <View style={styles.messagesContainer}>
                            <ScrollView
                                ref={scrollViewRef}
                                onContentSizeChange={() => scrollViewRef.current?.scrollToEnd({ animated: true })}
                                showsVerticalScrollIndicator={false}
                            >
                                {messages.length === 0 ? (
                                    <View style={styles.emptyChat}>
                                        <View style={styles.emptyChatIconContainer}>
                                            <Feather name="message-circle" size={48} color={COLORS.textMuted} />
                                        </View>
                                        <Text style={styles.emptyChatText}>Belum ada pesan</Text>
                                        <Text style={styles.emptyChatSubtext}>Kirim pesan untuk memulai percakapan dengan admin</Text>
                                    </View>
                                ) : (
                                    messages.map((msg, index) => (
                                        <View key={msg.id || index}>
                                            {/* Date separator */}
                                            {(index === 0 || formatDate(messages[index - 1]?.created_at) !== formatDate(msg.created_at)) && (
                                                <View style={styles.dateSeparator}>
                                                    <View style={styles.dateLine} />
                                                    <Text style={styles.dateText}>{formatDate(msg.created_at)}</Text>
                                                    <View style={styles.dateLine} />
                                                </View>
                                            )}

                                            <View style={[
                                                styles.messageBubble,
                                                msg.sender === 'member' ? styles.memberMessage : styles.adminMessage
                                            ]}>
                                                <Text style={[
                                                    styles.messageText,
                                                    msg.sender === 'member' ? styles.memberMessageText : styles.adminMessageText
                                                ]}>
                                                    {msg.message}
                                                </Text>
                                                <Text style={[
                                                    styles.messageTime,
                                                    msg.sender === 'member' ? styles.memberMessageTime : styles.adminMessageTime
                                                ]}>
                                                    {formatTime(msg.created_at)}
                                                </Text>
                                            </View>
                                        </View>
                                    ))
                                )}
                            </ScrollView>
                        </View>
                    )}

                    {/* Input Area - Now inside chat section */}
                    <KeyboardAvoidingView
                        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
                        style={styles.inputContainer}
                    >
                        <View style={styles.inputWrapper}>
                            <TextInput
                                style={styles.input}
                                placeholder="Tulis pesan..."
                                placeholderTextColor={COLORS.textMuted}
                                value={inputText}
                                onChangeText={setInputText}
                                multiline
                                maxLength={500}
                            />
                            <TouchableOpacity
                                style={[styles.sendBtn, !inputText.trim() && styles.sendBtnDisabled]}
                                onPress={sendMessage}
                                disabled={!inputText.trim() || sending}
                                activeOpacity={0.7}
                            >
                                {sending ? (
                                    <ActivityIndicator color="white" size="small" />
                                ) : (
                                    <Feather name="send" size={18} color="#fff" />
                                )}
                            </TouchableOpacity>
                        </View>
                    </KeyboardAvoidingView>
                </View>

                <View style={{ height: 100 }} />
            </ScrollView>
        </View>
    );
}

const styles = StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: COLORS.bgDark,
    },
    // Enhanced Header
    header: {
        flexDirection: 'row',
        alignItems: 'center',
        paddingHorizontal: 20,
        paddingBottom: 16,
        backgroundColor: COLORS.bgDarker,
        borderBottomLeftRadius: 24,
        borderBottomRightRadius: 24,
        shadowColor: COLORS.primary,
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.08,
        shadowRadius: 12,
        elevation: 6,
    },
    backButton: {
        width: 40,
        height: 40,
        justifyContent: 'center',
        alignItems: 'center',
        marginRight: 12,
        borderRadius: 20,
        backgroundColor: COLORS.bgDark,
    },
    headerContent: {
        flex: 1,
    },
    headerTitle: {
        fontSize: 22,
        fontWeight: '800',
        color: COLORS.textPrimary,
        marginBottom: 2,
        letterSpacing: -0.5,
    },
    headerSubtitle: {
        fontSize: 13,
        color: COLORS.textSecondary,
    },
    scrollView: {
        flex: 1,
    },
    scrollViewContent: {
        flexGrow: 1,
    },
    // Enhanced WhatsApp Card
    waCard: {
        margin: 20,
        borderRadius: 20,
        overflow: 'hidden',
        shadowColor: '#25D366',
        shadowOffset: { width: 0, height: 6 },
        shadowOpacity: 0.3,
        shadowRadius: 12,
        elevation: 8,
    },
    waCardGradient: {
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: '#25D366',
        padding: 20,
    },
    waIconContainer: {
        marginRight: 16,
    },
    waIcon: {
        width: 60,
        height: 60,
        borderRadius: 30,
        backgroundColor: 'rgba(255,255,255,0.25)',
        justifyContent: 'center',
        alignItems: 'center',
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.2,
        shadowRadius: 8,
        elevation: 4,
    },
    waContent: {
        flex: 1,
    },
    waTitle: {
        color: 'white',
        fontWeight: '800',
        fontSize: 18,
        marginBottom: 4,
        letterSpacing: -0.3,
    },
    waNumber: {
        color: 'rgba(255,255,255,0.95)',
        fontSize: 15,
        fontWeight: '600',
        marginBottom: 2,
    },
    waDesc: {
        color: 'rgba(255,255,255,0.8)',
        fontSize: 12,
        marginTop: 2,
    },
    waArrowContainer: {
        marginLeft: 8,
    },
    // Enhanced Chat Section
    chatSection: {
        marginHorizontal: 20,
        marginBottom: 20,
    },
    sectionHeader: {
        flexDirection: 'row',
        alignItems: 'center',
        marginBottom: 16,
    },
    sectionIconContainer: {
        width: 44,
        height: 44,
        borderRadius: 14,
        backgroundColor: COLORS.primary + '12',
        justifyContent: 'center',
        alignItems: 'center',
        marginRight: 12,
    },
    sectionTitle: {
        fontSize: 18,
        fontWeight: '800',
        color: COLORS.textPrimary,
        marginBottom: 2,
        letterSpacing: -0.3,
    },
    sectionSubtitle: {
        fontSize: 12,
        color: COLORS.textSecondary,
    },
    loadingContainer: {
        paddingVertical: 60,
        alignItems: 'center',
        justifyContent: 'center',
    },
    loadingText: {
        marginTop: 12,
        fontSize: 13,
        color: COLORS.textSecondary,
    },
    messagesContainer: {
        backgroundColor: COLORS.bgCard,
        borderRadius: 20,
        padding: 16,
        minHeight: 320,
        maxHeight: 400,
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.06,
        shadowRadius: 10,
        elevation: 3,
        borderWidth: 1,
        borderColor: COLORS.borderLight,
    },
    emptyChat: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
        paddingVertical: 80,
    },
    emptyChatIconContainer: {
        width: 80,
        height: 80,
        borderRadius: 40,
        backgroundColor: COLORS.bgDark,
        justifyContent: 'center',
        alignItems: 'center',
        marginBottom: 16,
    },
    emptyChatText: {
        fontSize: 16,
        fontWeight: '700',
        color: COLORS.textPrimary,
        marginBottom: 6,
    },
    emptyChatSubtext: {
        fontSize: 13,
        color: COLORS.textSecondary,
        textAlign: 'center',
        paddingHorizontal: 40,
    },
    dateSeparator: {
        flexDirection: 'row',
        alignItems: 'center',
        marginVertical: 16,
    },
    dateLine: {
        flex: 1,
        height: 1,
        backgroundColor: COLORS.borderLight,
    },
    dateText: {
        fontSize: 11,
        color: COLORS.textMuted,
        backgroundColor: COLORS.bgCard,
        paddingHorizontal: 12,
        paddingVertical: 4,
        borderRadius: 12,
        fontWeight: '600',
        marginHorizontal: 8,
    },
    messageBubble: {
        maxWidth: '80%',
        padding: 14,
        borderRadius: 18,
        marginVertical: 6,
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.08,
        shadowRadius: 6,
        elevation: 2,
    },
    memberMessage: {
        alignSelf: 'flex-end',
        backgroundColor: COLORS.primary,
        borderBottomRightRadius: 4,
    },
    adminMessage: {
        alignSelf: 'flex-start',
        backgroundColor: COLORS.bgDark,
        borderBottomLeftRadius: 4,
        borderWidth: 1,
        borderColor: COLORS.borderLight,
    },
    messageText: {
        fontSize: 14,
        lineHeight: 20,
    },
    memberMessageText: {
        color: 'white',
        fontWeight: '500',
    },
    adminMessageText: {
        color: COLORS.textPrimary,
    },
    messageTime: {
        fontSize: 10,
        marginTop: 6,
    },
    memberMessageTime: {
        color: 'rgba(255,255,255,0.75)',
        textAlign: 'right',
    },
    adminMessageTime: {
        color: COLORS.textMuted,
    },
    // Enhanced Input Area
    inputContainer: {
        marginTop: 12,
        paddingHorizontal: 0,
    },
    inputWrapper: {
        flexDirection: 'row',
        alignItems: 'flex-end',
        backgroundColor: COLORS.bgCard,
        borderRadius: 24,
        paddingHorizontal: 4,
        paddingVertical: 4,
        borderWidth: 1,
        borderColor: COLORS.borderLight,
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.05,
        shadowRadius: 8,
        elevation: 2,
    },
    input: {
        flex: 1,
        paddingHorizontal: 16,
        paddingVertical: 12,
        fontSize: 14,
        color: COLORS.textPrimary,
        maxHeight: 100,
    },
    sendBtn: {
        width: 40,
        height: 40,
        borderRadius: 20,
        backgroundColor: COLORS.primary,
        justifyContent: 'center',
        alignItems: 'center',
        marginLeft: 4,
        shadowColor: COLORS.primary,
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.3,
        shadowRadius: 6,
        elevation: 4,
    },
    sendBtnDisabled: {
        backgroundColor: COLORS.textMuted,
        shadowOpacity: 0,
        elevation: 0,
    },
});
